import type * as baseLocale from 'src/locales/compiled/en-US.json';

export type LocaleMessages = typeof baseLocale;
export type LocaleKey = keyof LocaleMessages;

export type SupportedLocale =
  | 'en-US'
  | 'de-DE'
  | 'es-LA'
  | 'fr-CA'
  | 'fr-FR'
  | 'hi-IN'
  | 'it-IT'
  | 'ja-JP'
  | 'ko-KR'
  | 'ms-MY'
  | 'pl-PL'
  | 'pt-BR'
  | 'zh-CN';

export interface LocaleDisplayInfo {
  name: string;
  displayName: string;
}

// TODO(evanmh): Include locale codes in languages API and expose languages API
// via GraphQL federation
// See: https://joinmodernhealth.atlassian.net/browse/CIRCLE-267

/**
 * A list of supported locale codes mapped to their English name and their
 * localized display name.
 *
 * Copied from https://api.staging.joinmodernhealth.com/languages/
 */
export const localeDisplayNames: Record<SupportedLocale, LocaleDisplayInfo> = {
  'en-US': {
    name: 'English',
    displayName: 'English',
  },
  'de-DE': {
    name: 'German',
    displayName: 'Deutsch',
  },
  'pl-PL': {
    name: 'Polish',
    displayName: 'Polski',
  },
  'it-IT': {
    name: 'Italian',
    displayName: 'Italiano',
  },
  'ja-JP': {
    name: 'Japanese',
    displayName: '日本語',
  },
  'pt-BR': {
    name: 'Portuguese',
    displayName: 'Português',
  },
  'ko-KR': {
    name: 'Korean',
    displayName: '한국어',
  },
  'es-LA': {
    name: 'Spanish',
    displayName: 'Español',
  },
  'hi-IN': {
    name: 'Hindi',
    displayName: 'हिन्दी',
  },
  'fr-FR': {
    name: 'French',
    displayName: 'Français',
  },
  'fr-CA': {
    name: 'French Canadian',
    displayName: 'Français québécois/canadien',
  },
  'ms-MY': {
    name: 'Malay',
    displayName: 'Bahasa Melayu',
  },
  'zh-CN': {
    name: 'Mandarin',
    displayName: '普通话',
  },
};

export interface LocaleDefinition extends LocaleDisplayInfo {
  locale: SupportedLocale;
}

export const locales: readonly LocaleDefinition[] = Object.entries(localeDisplayNames).map(
  ([locale, definition]) => ({
    locale: locale as SupportedLocale,
    ...definition,
  }),
);

export async function importMessages(locale: SupportedLocale): Promise<LocaleMessages> {
  switch (locale) {
    case 'en-US':
      return import('src/locales/compiled/en-US.json');
    case 'de-DE':
      return import('src/locales/compiled/de-DE.json');
    case 'es-LA':
      return import('src/locales/compiled/es-LA.json');
    case 'fr-FR':
      return import('src/locales/compiled/fr-FR.json');
    case 'fr-CA':
      return import('src/locales/compiled/fr-CA.json');
    case 'hi-IN':
      return import('src/locales/compiled/hi-IN.json');
    case 'it-IT':
      return import('src/locales/compiled/it-IT.json');
    case 'ja-JP':
      return import('src/locales/compiled/ja-JP.json');
    case 'ko-KR':
      return import('src/locales/compiled/ko-KR.json');
    case 'ms-MY':
      return import('src/locales/compiled/ms-MY.json');
    case 'pl-PL':
      return import('src/locales/compiled/pl-PL.json');
    case 'pt-BR':
      return import('src/locales/compiled/pt-BR.json');
    case 'zh-CN':
      return import('src/locales/compiled/zh-CN.json');
  }
}

/**
 * Splits a given language/locale code into the short and
 * specific variants and normalizes spacing and - usage.
 */
function getLocaleVariants(language: string): string[] {
  const trimmed = language.trim();

  if (language.includes('-') || language.includes('_')) {
    return [
      // en-US
      trimmed.replace('_', '-'),
      // en
      trimmed.split(/-|_/)[0],
    ];
  }

  // en
  return [trimmed];
}

/**
 * @returns a list of all the user's preferred locales, in short
 * and long variants.
 */
export function getBrowserLocales(): string[] {
  const languages = navigator.languages ?? [];

  const locales = languages.reduce((arr, language) => {
    arr.push(...getLocaleVariants(language));

    return arr;
  }, [] as string[]);

  return [...locales];
}

const SITE_LOCALE_STORAGE_KEY = 'site-locale';

export function saveLocale(locale: SupportedLocale) {
  localStorage.setItem(SITE_LOCALE_STORAGE_KEY, locale);
}

export function getSavedLocale() {
  const siteLocale = localStorage.getItem(SITE_LOCALE_STORAGE_KEY);

  if (locales.some(({ locale }) => locale === siteLocale)) {
    return siteLocale as SupportedLocale;
  }

  return null;
}

/**
 * Detect which supported locale best matches the user's preferred
 * locale.
 *
 * Returns `null` if no match is found.
 */
export function detectBrowserLocale(): SupportedLocale | null {
  const browserLocales = getBrowserLocales();

  for (const browserLocale of browserLocales) {
    for (const { locale } of locales) {
      const variants = getLocaleVariants(locale);

      if (variants.includes(browserLocale)) {
        return locale;
      }
    }
  }

  return null;
}
